import { useQuery } from '@apollo/client'
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next'

import Header from 'components/tour/events-list/header'
import Side from 'components/tour/tour-side'
import { GetEventsByTourSlugForSchemaQuery, GetStarBySlugQuery, GetTourBySlugQuery } from 'gql'
import { useGeolocation, useParseJSON, useQueryParams } from '~hooks'
import { addApolloState, initializeApollo } from '~services/apollo'
import { EventsList, MetaTags, EventsDateRangeFilter } from '~shared'
import { Grid, Heading, Layout, Mobile, Tooltip } from '~ui'
import {
  formatFeaturingArtists,
  getEventsDateRangeFilters,
  getImageUrl,
  getServerTimeNoSecondsInApiFormat,
} from '~utils'
import { getTourSchema } from '~utils/seo'
import { Experiment } from 'libs/optimize'
import NearEventsList from 'components/stars/NearEventsList'
import { ArtistMeta } from 'types/artist'
import { GET_STAR_BY_SLUG } from 'gql/stars/queries'
import { GET_TOUR_BY_SLUG_TOUR_PAGE } from 'gql/tour/queries'
import { GET_EVENTS_BY_TOUR_SLUG_FOR_SCHEMA } from 'gql/artwork/queries'

export const getServerSideProps: GetServerSideProps = async ({ query: { slug } }) => {
  const apollo = initializeApollo()

  const { data, error } = await apollo.query<GetTourBySlugQuery>({
    query: GET_TOUR_BY_SLUG_TOUR_PAGE,
    variables: {
      slug,
    },
  })

  return addApolloState(apollo, {
    notFound: error || !data.allTours.edges.length,
    props: {
      isPrivate: data?.allTours.edges[0]?.node.status === 'private',
    },
  })
}

const TourPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = () => {
  const {
    query: { slug, dateRange },
  } = useQueryParams()

  const { data } = useQuery<GetTourBySlugQuery>(GET_TOUR_BY_SLUG_TOUR_PAGE, {
    variables: { slug },
  })

  const dateTimeNow = getServerTimeNoSecondsInApiFormat(true)

  const { data: tourEventsData, loading: tourEventsLoading } =
    useQuery<GetEventsByTourSlugForSchemaQuery>(GET_EVENTS_BY_TOUR_SLUG_FOR_SCHEMA, {
      variables: {
        first: 20,
        tourSlug: slug,
        eventStartDate_Gte: dateTimeNow,
      },
    })

  const artistSlug = data?.allTours.edges[0].node?.artistSlug

  const { data: artist } = useQuery<GetStarBySlugQuery>(GET_STAR_BY_SLUG, {
    variables: { slug: artistSlug },
    skip: !data || !artistSlug,
  })

  const hideTevoLink = useParseJSON<ArtistMeta>(
    artist?.allArtists.edges[0].node?.meta
  )?.hideTEvoTickets

  const { title, tourPhoto, description, artistFullName, status, featuringArtists } =
    data.allTours.edges[0].node

  const isTourPrivate = status === 'private'

  const subTitle = formatFeaturingArtists(featuringArtists)

  const { coordinates } = useGeolocation()

  const tourEventListVariables = {
    tourSlug: slug,
    ...getEventsDateRangeFilters(dateRange),
    announceDateGte: isTourPrivate ? dateTimeNow : undefined,
    statusIn: isTourPrivate ? 'private,public' : 'public',
  }

  return (
    <Layout>
      <Mobile>
        <Tooltip />
      </Mobile>
      <MetaTags
        imageSrc={tourPhoto}
        description={description}
        title={`${artistFullName} • ${title} ${subTitle || ''}`}
        schema={
          tourEventsLoading
            ? undefined
            : getTourSchema(
                tourEventsData.allArtworks.edges.map(
                  ({ node: { artworkOrTourOrArtistImage, ...event } }) => ({
                    ...event,
                    imageSrc: artworkOrTourOrArtistImage,
                    mainArtist: {
                      fullName: artistFullName,
                    },
                  })
                )
              )
        }
      />
      <Layout.HomeHeader fluid />
      <Layout.Main>
        <Layout.Main.Sidebar>
          <Side />
        </Layout.Main.Sidebar>
        <Layout.Main.Content>
          <Layout.Main.Content.Widget>
            <Grid gap={24}>
              <Header
                imageSrc={getImageUrl(tourPhoto, 256, 256)}
                title={artistFullName}
                subtitle={title}
              />
              <Experiment name="sellingFastLabel" activateEvent="optimize.activate1" loader={null}>
                <NearEventsList
                  variables={tourEventListVariables}
                  location={coordinates}
                  hideTevoLink={hideTevoLink}
                />
              </Experiment>

              <EventsList
                hideTevoLink={hideTevoLink}
                title={
                  <Grid
                    gridAutoFlow="column"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={18}
                  >
                    <Heading Tag="h2" size="h4" fontWeight={500}>
                      Events
                    </Heading>
                    <EventsDateRangeFilter />
                  </Grid>
                }
                queryOptions={{
                  variables: { ...tourEventListVariables, first: 20 },
                }}
                options={{ venueName: true }}
              />
            </Grid>
          </Layout.Main.Content.Widget>
        </Layout.Main.Content>
      </Layout.Main>
      <Layout.Footer />
    </Layout>
  )
}

export default TourPage
